import { useState, useCallback, useMemo } from 'react';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { 
  Button, 
  CircularProgress, 
  Container, 
  Snackbar, 
  Typography, 
  Box, 
  IconButton, 
  ToggleButtonGroup, 
  ToggleButton, 
  Tooltip, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  MenuItem, 
  Menu, 
  Checkbox, 
  FormControlLabel, 
  FormGroup, 
  DialogContentText, 
  Switch 
} from '@mui/material';

import { 
  Save, 
  ExpandMore, 
  ChevronRight, 
  UnfoldMore, 
  UnfoldLess, 
  ViewCompact, 
  ViewComfy, 
  ContentCopy, 
  ContentPaste, 
  NotificationAddSharp, 
} from '@mui/icons-material';
import {
  Country,
  Product,
  TChecklistTrackAndTraceSubmitItem,
  TGetLinkChecklistToProductItem,
  TLinkChecklistToProductResponse,
  TUnlinkChecklistToProductPayload,
  linkChecklistToProduct,
  unlinkChecklistToProduct,
  updateCountry,
  submitTrackAndTraceNotificationConfig as submitTrackAndTraceNotificationConfigAPI
} from 'api';
import { generateActionsGridColDef } from 'components/Table/Actions';
import { TableHeader } from 'components/Table/Header';
import { generateImageGridColDef } from 'components/Table/Image';
import { CountrySelector } from 'components/CountrySelector/CountrySelector';
import { useIntl } from 'react-intl';
import { validateCalculation, validateSubHeading, generateInputTextDef } from 'components/Table/InputText';
import { TextField, Grid } from '@mui/material';
import { isEmpty, map, reduce, some, filter } from 'lodash';
import { useBrandsAndCountries } from './useBrandsAndCountries';
import { useProductsData } from './useProductsData';
import { ProductWithGrouping } from './types';
import { useTrackAndTraceCheckLists, useTrackAndTraceProductQuestions } from './useTrackAndTraceCheckLists'
import { useNotificationConfigs } from './useNotificationConfigs';
import { TSubmitTrackAndTraceNotificationConfigPayload } from 'api/type';

// Custom hook for copied products
const useProductsCopyPaste = () => {
  const [copiedProducts, setCopiedProducts] = useState<Product[] | null>(null);
  const [sourceCountry, setSourceCountry] = useState<string | null>(null);
  
  const copyProducts = useCallback((products: Product[], countryName: string) => {
    // Filter out category rows and clone products
    const productsToCopy = products
      .filter(product => !product.__isCategory)
      .map(product => ({...product}));
      
    setCopiedProducts(productsToCopy);
    setSourceCountry(countryName);
  }, []);
  
  const hasCopiedProducts = !!copiedProducts && copiedProducts.length > 0;
  
  return {
    copiedProducts,
    sourceCountry,
    copyProducts,
    hasCopiedProducts
  };
};


export function ProductsPage() {
  const intl = useIntl();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isHaccpSaving, setIsHaccpSaving] = useState(false);
  const [isTrackAndTraceNotificationSaving, setIsTrackAndTraceNotificationSaving] = useState(false);
  const [message, setMessage] = useState<string | undefined>(undefined);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [pasteMenuAnchorEl, setPasteMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [haccpModalOpen, setHaccpModalOpen] = useState(false);
  const [confirmDisableModalOpen, setConfirmDisableModalOpen] = useState(false);
  const [trackAndTraceActiveProduct, setTrackAndTraceActiveProduct] = useState<TGetLinkChecklistToProductItem | null>(null);
  const [checkListLinkProductState, setCheckListLinkProductState] = useState<{ [key: string]: boolean }>({});
    
  const {
    brands,
    countries,
    setCountries,
    activeBrand,
    setActiveBrand,
    activeCountry,
    setActiveCountry
  } = useBrandsAndCountries();

  const {
    trackAndTraceChecklists,
    trackAndTraceChecklistsMapByResourceKey,
    hasCheckListquestionConfig,
  } = useTrackAndTraceCheckLists(
    {
      brand: activeBrand,
      country: activeCountry
    }
  );

  //linked checklist to product
  const {
    trackAndTraceProductQuestionsMap,
    linkProductQuestions: updateLinkProductQuestionsState,
    unlinkProductQuestions: updateUnlinkProductQuestionsState
  } = useTrackAndTraceProductQuestions(
    {
      brand: activeBrand,
      country: activeCountry
    }
  );

  const {
    data,
    groupedProducts,
    isLoading,
    denseView,
    handleCellEditCommit,
    handleAddProduct,
    handleDeleteProduct,
    toggleCategoryExpansion,
    expandAllCategories,
    collapseAllCategories,
    toggleDenseView,
  } = useProductsData(activeCountry);
  
  const {
    copiedProducts,
    sourceCountry,
    copyProducts,
    hasCopiedProducts
  } = useProductsCopyPaste();

  const {
    trackAndTraceNotificationConfig,
    trackAndTraceNotificationOpen,
    closeTrackAndTraceNotificationModal,
    isTrackAndTraceNotificationConfigValid,
    updateTrackAndTraceNotificationConfig,
    setTrackAndTraceNotificationOpen,
    isNotificationLoading
  } = useNotificationConfigs(
    {
      brand: activeBrand,
      country: activeCountry
    }
  );  

  const handleSave = useCallback(async () => {
    if (!activeCountry) return;
    
    setIsUpdating(true);
    try {
      // Filter out category rows and only include actual product data
      const productsToSave = data.filter(product => !product.__isCategory);
      
      await updateCountry({ ...activeCountry, products: productsToSave });
      setMessage(intl.formatMessage({ id: 'app.button.save.successMessage' }));
      
      // Update the countries state with the new data
      setCountries(prevCountries => {
        return prevCountries.map(country => {
          if (country.id === activeCountry.id) {
            return { ...country, products: productsToSave };
          }
          return country;
        });
      });
    } catch (error) {
      console.error('Failed to save products:', error);
      setMessage(intl.formatMessage({ id: 'app.button.save.failureMessage' }));
    } finally {
      setIsUpdating(false);
    }
  }, [activeCountry, data, intl, setCountries]);

  // Handle opening copy dialog
  const handleOpenCopyDialog = useCallback(() => {
    setCopyDialogOpen(true);
  }, []);

  // Handle copying products
  const handleCopyProducts = useCallback(() => {
    if (!activeCountry) return;
    
    copyProducts(data, activeCountry.id);
    setCopyDialogOpen(false);
    
    setMessage(intl.formatMessage(
      { id: 'app.message.productsCopied', defaultMessage: 'Products copied from {country}' },
      { country: activeCountry.id }
    ));
  }, [activeCountry, copyProducts, data, intl]);

  // Handle opening paste menu
  const handlePasteButtonClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setPasteMenuAnchorEl(event.currentTarget);
  }, []);

  // Handle closing paste menu
  const handlePasteMenuClose = useCallback(() => {
    setPasteMenuAnchorEl(null);
  }, []);

  // Handle pasting products to selected country
  const handlePasteProducts = useCallback(async (targetCountry: Country) => {
    if (!copiedProducts || !targetCountry) return;
    
    setIsUpdating(true);
    setPasteMenuAnchorEl(null);
    
    try {
      // Get existing products or empty array
      const existingProducts = targetCountry.products || [];
      
      // Create new product instances with new IDs to avoid conflicts
      const newProducts = copiedProducts.map(product => ({
        ...product,
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}` // Generate unique IDs
      }));
      
      // Combine existing and new products
      const updatedProducts = [...existingProducts, ...newProducts];
      
      // Update the country with new products
      await updateCountry({
        id: targetCountry.id,
        brandId: targetCountry.brandId,
        products: updatedProducts
      });
      
      // Update local state if the active country is the target country
      if (activeCountry && activeCountry.id === targetCountry.id) {
        // Refresh the current data
        setActiveCountry({
          ...targetCountry,
          products: updatedProducts
        });
      }
      
      // Update countries list
      setCountries(prevCountries => 
        prevCountries.map(country => 
          country.id === targetCountry.id 
            ? { ...country, products: updatedProducts } 
            : country
        )
      );
      
      setMessage(intl.formatMessage(
        { id: 'app.message.productsPasted', defaultMessage: 'Products pasted to {country}' },
        { country: targetCountry.id }
      ));
    } catch (error) {
      console.error('Failed to paste products:', error);
      setMessage(intl.formatMessage({ id: 'app.message.pasteError', defaultMessage: 'Failed to paste products' }));
    } finally {
      setIsUpdating(false);
    }
  }, [activeCountry, copiedProducts, intl, setActiveCountry, setCountries]);

  // Custom renderer for category cells
  const renderCategoryCell = useCallback((params: GridRenderCellParams) => {
    const isCategory = (params.row as ProductWithGrouping).__isCategory;
    const isExpanded = (params.row as ProductWithGrouping).__categoryExpanded;
    
    if (isCategory) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box 
            sx={{ 
              cursor: 'pointer', 
              display: 'flex', 
              alignItems: 'center',
              '&:hover': { opacity: 0.7 } 
            }}
            onClick={() => toggleCategoryExpansion(params.row.id as string)}
          >
            {isExpanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', ml: 1 }}>
              {params.value}
            </Typography>
          </Box>
        </Box>
      );
    }
    
    return params.value;
  }, [toggleCategoryExpansion]);

  // Open modal for a single product
  const openHaccpModal = useCallback((product: TGetLinkChecklistToProductItem) => {
    setTrackAndTraceActiveProduct(product);
    const questionsLinkedToChecklist = product.questions || [];
    setCheckListLinkProductState(
      trackAndTraceChecklists.reduce((acc, field) => {
        acc[field.nameResourceKey] = some(questionsLinkedToChecklist, question => question.nameResourceKey === field.nameResourceKey);
        return acc;
      }, {} as { [key: string]: boolean })
    );
    setHaccpModalOpen(true);
  }, [trackAndTraceChecklists]);

  const openConfirmDisableModal = useCallback((product: TGetLinkChecklistToProductItem) => {
    setConfirmDisableModalOpen(true);
    setTrackAndTraceActiveProduct(product);
  }, []);

  const onToggleCheckListLinkProduct = useCallback((product: TGetLinkChecklistToProductItem) => {
    if (!isEmpty(trackAndTraceProductQuestionsMap[product.productId])) {
      openConfirmDisableModal(product);
    } else {
      openHaccpModal(product);
    }
  }, [trackAndTraceProductQuestionsMap, openConfirmDisableModal, openHaccpModal]);

  // Handle checkbox change
  const handleCheckListLinkProductChange = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setCheckListLinkProductState(prev => ({ ...prev, [key]: e.target.checked }));
  };

  const handleUnLinkProductChecklist = useCallback(async (product: TGetLinkChecklistToProductItem | null) => {
    if (!product) {
      setConfirmDisableModalOpen(false);
      return;
    }
    if (!activeBrand) {
      return
    }

    try {
      const idObjects: TUnlinkChecklistToProductPayload = product.questions?.map(q => {
        return {
          id: q.id
        }
      });
      await unlinkChecklistToProduct(product.productId, idObjects, activeBrand.id);
      updateUnlinkProductQuestionsState(product.productId);
      // close modal
      setConfirmDisableModalOpen(false);
    } catch (e) {
      setMessage('Failed to unlink HACCP tracking fields');
    }
  }, [updateUnlinkProductQuestionsState, activeBrand]);

  // Save HACCP config for a product or bulk
  const handleSaveLinkProductChecklist = useCallback(async () => {
    if (!trackAndTraceActiveProduct || !activeBrand || !activeCountry) return;
    //required fields
    const requiredFieldsNameResourceKey = map(filter(trackAndTraceChecklists,field => field.required), field => field.nameResourceKey);
    //other enabled fields
    const enabledFields = filter(Object.keys(checkListLinkProductState), k => checkListLinkProductState[k]);
    //all fields to be linked
    const allFields = [...requiredFieldsNameResourceKey, ...enabledFields];
    setIsHaccpSaving(true);
    try {
      const {
        productId,
        productName,
        category
      } = trackAndTraceActiveProduct;

      const products: TChecklistTrackAndTraceSubmitItem[] = map(allFields, nameResourceKey => ({
        productId,
        category: category || '',
        productName,
        nameResourceKey,
        required: trackAndTraceChecklistsMapByResourceKey[nameResourceKey].required
      }));

      const linkChecklistToProductResponse: TLinkChecklistToProductResponse = await linkChecklistToProduct({
        brand: activeBrand.id,
        countryIsoCode: activeCountry.id,
        products
      });

      const checkListProductIdByResourceKey = reduce(linkChecklistToProductResponse.products, (acc, product) => {
        acc[product.nameResourceKey] = product.id;
        return acc;
      }, {} as { [key: string]: number });

      const enabledQuestions = map(allFields, nameResourceKey => ({
        id: checkListProductIdByResourceKey[nameResourceKey],
        nameResourceKey,
        type: trackAndTraceChecklistsMapByResourceKey[nameResourceKey].type,
        required: trackAndTraceChecklistsMapByResourceKey[nameResourceKey].required
      }));

      updateLinkProductQuestionsState(productId, enabledQuestions);

      setMessage('HACCP tracking field updated');
    } catch (e) {
      setMessage('Failed to update HACCP tracking fields');
    } finally {
      setIsHaccpSaving(false);
      setHaccpModalOpen(false);
    }
  }, [
    activeCountry,
    activeBrand, 
    trackAndTraceChecklists,
    trackAndTraceChecklistsMapByResourceKey, 
    checkListLinkProductState,
    trackAndTraceActiveProduct,
    updateLinkProductQuestionsState,
    setMessage,
    setIsHaccpSaving,
    setHaccpModalOpen
  ]);

  const submitTrackAndTraceNotificationConfig = useCallback(async () => {
    if (!activeBrand || !activeCountry) return;
    try {
    setIsTrackAndTraceNotificationSaving(true);
    let payload: TSubmitTrackAndTraceNotificationConfigPayload = {
      brand: activeBrand.id,
      countryIsoCode: activeCountry.id,
      preExpiredReminderEnabled: false,
      postExpiredReminderEnabled: false,
      preExpiredReminderInMinutes: 0,
      postExpiredReminderIntervalInMinutes: 0,
    }
    if(trackAndTraceNotificationConfig.enabled) {
      payload = {
        ...payload,
        preExpiredReminderEnabled: trackAndTraceNotificationConfig.preExpiredReminderEnabled,
        postExpiredReminderEnabled: trackAndTraceNotificationConfig.postExpiredReminderEnabled,
        preExpiredReminderInMinutes: trackAndTraceNotificationConfig.preExpiredReminderInMinutes,
        postExpiredReminderIntervalInMinutes: trackAndTraceNotificationConfig.postExpiredReminderIntervalInMinutes,
      };
    }
    await submitTrackAndTraceNotificationConfigAPI(payload);
    } catch (e) {
      setMessage('Failed to update Track & Trace Notification Config');
    } finally {
      setIsTrackAndTraceNotificationSaving(false);
      setTrackAndTraceNotificationOpen(false);
    }
  }, [
    activeBrand,
    activeCountry,
    trackAndTraceNotificationConfig,
    setTrackAndTraceNotificationOpen
  ]);

  const columns: GridColDef[] = useMemo(() => {
    const columns = [
      {
        field: 'id',
        headerName: intl.formatMessage({ id: 'app.table.heading.id' }),
        width: 50
      },
      {
        field: 'category',
        headerName: `${intl.formatMessage({ id: 'app.table.heading.category' })} (${activeCountry?.primaryLanguage})`,
        width: 180,
        editable: true,
        renderCell: renderCategoryCell
      },
      {
        field: 'heading',
        headerName: `${intl.formatMessage({ id: 'app.table.heading.heading' })} (${activeCountry?.primaryLanguage})`,
        width: 200,
        editable: true,
      },
      generateInputTextDef({
        field: 'subHeading',
        headerName: intl.formatMessage({ id: 'app.table.heading.subHeading' }),
        width: 200,
        editable: true,
        errorMessageType: 'SUB_HEADING_MAX_LENGTH',
        preProcessEditCellProps: validateSubHeading
      }),
      generateImageGridColDef({
        field: 'image',
        headerName: intl.formatMessage({ id: 'app.table.heading.image' }),
        width: 150,
        activeBrand,
      }),
      generateInputTextDef({
        field: 'calculationReady',
        headerName: intl.formatMessage({ id: 'app.table.heading.ready' }),
        width: 80,
        editable: true,
        errorMessageType: 'INVALID',
        preProcessEditCellProps: validateCalculation
      }),
      generateInputTextDef({
        field: 'calculationDiscard',
        headerName: intl.formatMessage({ id: 'app.table.heading.discard' }),
        width: 80,
        editable: true,
        errorMessageType: 'INVALID',
        preProcessEditCellProps: validateCalculation
      }),
      ...(activeCountry?.secondaryLanguage?.length ? [
        {
          field: 'category_1',
          headerName: `${intl.formatMessage({ id: 'app.table.heading.category' })} (${activeCountry?.secondaryLanguage})`,
          width: 150,
          editable: true,
        },
        {
          field: 'heading_1',
          headerName: `${intl.formatMessage({ id: 'app.table.heading.heading' })} (${activeCountry?.secondaryLanguage})`,
          width: 200,
          editable: true,
        },
        {
          field: 'subHeading_1',
          headerName: `${intl.formatMessage({ id: 'app.table.heading.subHeading' })} (${activeCountry?.secondaryLanguage})`,
          width: 200,
          editable: true,
        },
      ] : []),
      generateActionsGridColDef({
        headerName: intl.formatMessage({id: 'app.table.heading.actions'}),
        onDelete: handleDeleteProduct
      }),
    ]

    // Add Checklist Linked column if enabled
    if (hasCheckListquestionConfig) {
      // Find the index of the actions column (last column)
      const actionsColIdx = columns.findIndex(col => col.field === 'actions');
      // If found, insert before it; otherwise, push to the end
      const insertIdx = actionsColIdx > -1 ? actionsColIdx : columns.length;
      columns.splice(insertIdx, 0, {
        field: 'trackAndTraceChecklistLinked',
        headerName: 'Checklist Linked',
        width: 250,
        renderCell: (params: GridRenderCellParams) => {
          const product = params.row as ProductWithGrouping;
          if (product.__isCategory) {
            return null
          }

          const productId = product.id;
          const linkedChecklistQuestionsByProductId = trackAndTraceProductQuestionsMap[productId];
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Switch checked={!isEmpty(linkedChecklistQuestionsByProductId)} onChange={() => onToggleCheckListLinkProduct({
                productId,
                category: product.category,
                productName: product.heading,
                questions: linkedChecklistQuestionsByProductId || []
              })} />
            </Box>
          );
        },
      });
    }

    return columns;
  }, [
    activeCountry,
    activeBrand,
    intl,
    hasCheckListquestionConfig,
    trackAndTraceProductQuestionsMap,
    handleDeleteProduct,
    onToggleCheckListLinkProduct,
    renderCategoryCell,
  ]);

  // Filter out hidden rows
  const visibleRows = useMemo(() => filter(groupedProducts, row => !row.__hidden), [groupedProducts]);

  return (
    <Container style={{ marginTop: '80px', minHeight: '300px' }}>
      <TableHeader
        title={`${intl.formatMessage({id: 'app.heading.products'})} (${activeBrand?.id} / ${activeCountry?.id})`}
        onAdd={handleAddProduct}
        shouldSkipOnAddId={true}>
        <>
          <CountrySelector
            activeBrand={activeBrand}
            brands={brands}
            activeCountry={activeCountry}
            countries={countries}
            setActiveBrand={setActiveBrand}
            setActiveCountry={setActiveCountry}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
            <Tooltip title={intl.formatMessage({ id: 'app.button.expandAll.title', defaultMessage: 'Expand All' })}>
              <IconButton onClick={expandAllCategories} size="small" sx={{ mr: 0.5 }}>
                <UnfoldMore />
              </IconButton>
            </Tooltip>
            <Tooltip title={intl.formatMessage({ id: 'app.button.collapseAll.title', defaultMessage: 'Collapse All' })}>
              <IconButton onClick={collapseAllCategories} size="small" sx={{ mr: 1 }}>
                <UnfoldLess />
              </IconButton>
            </Tooltip>
            
            <ToggleButtonGroup
              size="small"
              value={denseView}
              exclusive
              onChange={(_, value) => toggleDenseView(value)}
              sx={{ mr: 1 }}
            >
              <ToggleButton 
                value={true} 
                aria-label="compact view"
                title={intl.formatMessage({ id: 'app.button.compactView.title', defaultMessage: 'Compact View' })}
              >
                <ViewCompact fontSize="small" />
              </ToggleButton>
              <ToggleButton 
                value={false} 
                aria-label="comfortable view"
                title={intl.formatMessage({ id: 'app.button.comfortableView.title', defaultMessage: 'Comfortable View' })}
              >
                <ViewComfy fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
            
            <Tooltip title={intl.formatMessage({ id: 'app.button.copyProducts.title', defaultMessage: 'Copy Products' })}>
              <IconButton 
                onClick={handleOpenCopyDialog} 
                size="small" 
                sx={{ mr: 0.5 }}
                disabled={!activeCountry || data.filter(p => !p.__isCategory).length === 0}
              >
                <ContentCopy fontSize="small" />
              </IconButton>
            </Tooltip>
            
            <Tooltip title={intl.formatMessage({ id: 'app.button.pasteProducts.title', defaultMessage: 'Paste Products' })}>
              <span>
                <IconButton 
                  onClick={handlePasteButtonClick}
                  size="small" 
                  sx={{ mr: 1 }}
                  disabled={!hasCopiedProducts || !activeCountry}
                >
                  <ContentPaste fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            { !!hasCheckListquestionConfig && <Tooltip title={intl.formatMessage({ id: 'app.button.notificationConfig.title', defaultMessage: 'Track & Trace Notification Config' })}>
              <span>
                <IconButton 
                  onClick={() => setTrackAndTraceNotificationOpen(true)}
                  size="small" 
                  sx={{ mr: 1 }}
                >
                  <NotificationAddSharp fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            }
          </Box>
          <Button
            sx={{ marginRight: '7px' }}
            variant="outlined"
            disabled={isUpdating}
            startIcon={isUpdating ? <CircularProgress size={20} /> : <Save />}
            onClick={handleSave}
          >
            {intl.formatMessage({ id: 'app.button.save.title' })}
          </Button>
        </>
      </TableHeader>
      
      {/* Copy Confirmation Dialog */}
      <Dialog open={copyDialogOpen} onClose={() => setCopyDialogOpen(false)}>
        <DialogTitle>
          {intl.formatMessage({ id: 'app.dialog.copyProducts.title', defaultMessage: 'Copy Products' })}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {intl.formatMessage(
              { id: 'app.dialog.copyProducts.message', defaultMessage: 'Copy all products from {country}?' },
              { country: activeCountry?.id || '' }
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {intl.formatMessage(
              { id: 'app.dialog.copyProducts.count', defaultMessage: 'This will copy {count} products.' },
              { count: data.filter(p => !p.__isCategory).length }
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyDialogOpen(false)}>
            {intl.formatMessage({ id: 'app.button.cancel', defaultMessage: 'Cancel' })}
          </Button>
          <Button onClick={handleCopyProducts} variant="contained" color="primary">
            {intl.formatMessage({ id: 'app.button.copy', defaultMessage: 'Copy' })}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Paste Menu */}
      <Menu
        anchorEl={pasteMenuAnchorEl}
        open={Boolean(pasteMenuAnchorEl)}
        onClose={handlePasteMenuClose}
      >
        <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: 'text.secondary' }}>
          {intl.formatMessage({ id: 'app.menu.pasteProducts.selectCountry', defaultMessage: 'Select destination country:' })}
        </Typography>
        {countries
          .filter(country => country.brandId === activeBrand?.id)
          .map(country => (
            <MenuItem 
              key={country.id} 
              onClick={() => handlePasteProducts(country)}
              disabled={activeCountry?.id === country.id && sourceCountry === country.id}
            >
              {country.id}
            </MenuItem>
          ))}
      </Menu>

      {/* HACCP Modal */}
      <Dialog open={haccpModalOpen} onClose={() => setHaccpModalOpen(false)}>
        <DialogTitle>{'Add Linked Track & Trace'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Select Checklist fields for linking to {trackAndTraceActiveProduct?.productName}
          </DialogContentText>
          <FormGroup>
              <FormControlLabel
                key={'enabled'}
                control={<Switch checked={true} disabled={true} />}
                label={'Enabled'}
              />
            {
              map(trackAndTraceChecklists, field => (
                <FormControlLabel
                  key={field.nameResourceKey}
                  disabled={field.required}
                  control={<Checkbox checked={field.required || !!checkListLinkProductState[field.nameResourceKey]} onChange={handleCheckListLinkProductChange(field.nameResourceKey)} />}
                  label={field.name}
                />
              ))
            }
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHaccpModalOpen(false)}>{intl.formatMessage({ id: 'app.button.cancel', defaultMessage: 'Cancel' })}</Button>
          <Button onClick={handleSaveLinkProductChecklist} variant="contained" disabled={isHaccpSaving}>
            {intl.formatMessage({ id: 'app.button.save.title', defaultMessage: 'Save' })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unlink checklist confirmation dialog */}
      <Dialog open={confirmDisableModalOpen} onClose={() => setConfirmDisableModalOpen(false)}>
        <DialogTitle>
          {intl.formatMessage({ id: 'app.dialog.confirmDisable.title', defaultMessage: 'Confirm Disable' })}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {intl.formatMessage(
              { id: 'app.dialog.confirmDisable.message', defaultMessage: 'Are you sure you want to unlink Track & Trace questions for this product - {productName}?' },
              { productName: trackAndTraceActiveProduct?.productName }
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDisableModalOpen(false)}>
            {intl.formatMessage({ id: 'app.button.cancel', defaultMessage: 'Cancel' })}
          </Button>
          <Button onClick={() => handleUnLinkProductChecklist(trackAndTraceActiveProduct)} variant="contained" color="primary">
            {intl.formatMessage({ id: 'app.button.confirm', defaultMessage: 'Confirm' })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Track & Trace Notification Modal */}
      <Dialog open={trackAndTraceNotificationOpen} onClose={closeTrackAndTraceNotificationModal}>
        <DialogTitle>{intl.formatMessage({ id: 'app.dialog.trackAndTraceNotification.title', defaultMessage: 'Track & Trace Notification Config' })}</DialogTitle>
        <DialogContent>
            {isNotificationLoading ? <CircularProgress size={20} /> : (
            <>
              <FormControlLabel
              control={
                <Switch
                  checked={trackAndTraceNotificationConfig.enabled}
                  onChange={(_, checked) => updateTrackAndTraceNotificationConfig('enabled', checked)}
                />
              }
              label="Enabled"
              />
              { trackAndTraceNotificationConfig.enabled ? <Grid container>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Notification Rules</Typography>
                {/* Notification Before Discard */}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={trackAndTraceNotificationConfig.preExpiredReminderEnabled}
                        onChange={(_, checked) => updateTrackAndTraceNotificationConfig('preExpiredReminderEnabled', checked)}
                      />
                    }
                    label="Notification Before Discard"
                  />
                  {trackAndTraceNotificationConfig.preExpiredReminderEnabled && (
                    <TextField
                      label="Minutes before discard"
                      type="number"
                      value={trackAndTraceNotificationConfig.preExpiredReminderInMinutes}
                      onChange={e => updateTrackAndTraceNotificationConfig('preExpiredReminderInMinutes', Number(e.target.value))}
                      size="small"
                      sx={{ mb: 2, ml: 2, width: 180 }}
                    />
                  )}
                </Grid>
                {/* Notification Every X Minutes After Discard */}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={trackAndTraceNotificationConfig.postExpiredReminderEnabled}
                        onChange={(_, checked) => updateTrackAndTraceNotificationConfig('postExpiredReminderEnabled', checked)}
                      />
                    }
                    label="Notification Every X Minutes After Discard"
                  />
                  {trackAndTraceNotificationConfig.postExpiredReminderEnabled && (
                    <TextField
                      label="Minutes interval after discard"
                      type="number"
                      value={trackAndTraceNotificationConfig.postExpiredReminderIntervalInMinutes}
                      onChange={e => updateTrackAndTraceNotificationConfig('postExpiredReminderIntervalInMinutes', Number(e.target.value))}
                      size="small"
                      sx={{ mb: 2, ml: 2, width: 180 }}
                    />
                  )}
                </Grid>
              </Grid> : null
              }
              </>
            )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeTrackAndTraceNotificationModal}>
            {intl.formatMessage({ id: 'app.button.cancel', defaultMessage: 'Cancel' })}
          </Button>
          <Button
            variant="contained"
            onClick={submitTrackAndTraceNotificationConfig}
            disabled={isTrackAndTraceNotificationSaving || !isTrackAndTraceNotificationConfigValid || isNotificationLoading}
          >
            {isTrackAndTraceNotificationSaving ? <CircularProgress size={20} /> : intl.formatMessage({ id: 'app.button.save', defaultMessage: 'Save' })}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!message}
        autoHideDuration={3000}
        onClose={() => setMessage(undefined)}
        message={message}
      />
      
      <DataGrid
        loading={isLoading}
        autoHeight
        rows={visibleRows}
        columns={columns}
        onCellEditCommit={handleCellEditCommit}
        getRowClassName={(params) => {
          return (params.row as ProductWithGrouping).__isCategory ? 'category-row' : '';
        }}
        density={denseView ? 'compact' : 'standard'}
        sx={{
          '& .category-row': {
            backgroundColor: '#f5f5f5',
            fontWeight: 'bold',
          },
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
        }}
      />
    </Container>
  );
}
